import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { IoMdArrowBack } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import { hrRecordsApi } from "../Services/hrRecordsApi";
import {
  buildEmailConfigOptions,
  buildEmailTemplateOptions,
  formatDateTimeIST,
  getApiErrorMessage,
  getSelectedOption,
  truthyLabel,
} from "./hrRecordsHelpers";
import "../forms/UserForm.css";
import "../tables/User.css";
import "../Views/ApprovedPrView.css";
import "../Views/voyageView.css";
import "./hrRecords.css";

const MODULE_META = {
  training: {
    title: "Training Reminder Settings",
    label: "Training",
    fields: [
      "email_config_id",
      "email_temp_id",
      "module_label",
      "notify_in_app",
      "notify_employee",
      "notify_roles",
      "reminder_days_before",
      "overdue_repeat_days",
      "is_off",
    ],
  },
  medical: {
    title: "Medical Reminder Settings",
    label: "Medical",
    fields: [
      "email_config_id",
      "email_temp_id",
      "module_label",
      "notify_in_app",
      "notify_employee",
      "notify_roles",
      "reminder_days_before",
      "overdue_repeat_days",
      "is_off",
    ],
  },
  compliance: {
    title: "Compliance Reminder Settings",
    label: "Compliance",
    fields: [
      "email_config_id",
      "notify_owner",
      "notify_digest",
      "notify_roles",
      "extra_emails",
      "reminder_days_before",
      "overdue_repeat_days",
      "horizon_days",
      "max_rows",
      "is_off",
      "enabled_modules",
    ],
  },
  hr_compliance: {
    title: "HR Compliance Reminder Settings",
    label: "HR Compliance",
    fields: [
      "email_config_id",
      "email_temp_id",
      "notify_in_app",
      "notify_employee",
      "notify_roles",
      "reminder_days_before",
      "overdue_repeat_days",
      "is_off",
    ],
  },
  inventory_reorder: {
    title: "Inventory Reorder Reminder Settings",
    label: "Inventory Reorder",
    fields: ["email_config_id", "email_temp_id", "frequency_days", "is_off"],
  },
  scheduled_report: {
    title: "Scheduled Report Sender Settings",
    label: "Scheduled Report",
    fields: [
      "variant",
      "module_label",
      "email_config_id",
      "email_temp_id",
      "send_date",
      "is_off",
      "format",
    ],
    usesVariant: true,
  },
  daily_progress_report: {
    title: "Daily Progress Report Notification Settings",
    label: "Daily Progress Report",
    fields: ["email_temp_id", "module_label"],
  },
};

const DEFAULT_MODULE_OPTIONS = Object.entries(MODULE_META).map(
  ([value, meta]) => ({
    value,
    label: meta.label,
  }),
);

const emptyConfig = {
  module_key: "training",
  module_label: "Training",
  variant: "",
  email_config_id: "",
  email_temp_id: "",
  notify_roles: "",
  extra_emails: "",
  notify_in_app: true,
  notify_employee: false,
  notify_owner: false,
  notify_digest: false,
  is_off: false,
  reminder_days_before: "",
  overdue_repeat_days: 7,
  frequency_days: "",
  next_run_date: "",
  send_date: "",
  horizon_days: "",
  max_rows: "",
  effective_sender_email: "",
  sender_is_fallback: false,
  email_config_email: "",
  template_name: "",
  template_subject: "",
  template_body: "",
  template_to_emails: "",
  options: {
    format: "CSV",
    enabled_modules: "",
  },
};

const yesNoOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const formatOptions = [
  { value: "CSV", label: "CSV" },
  { value: "Excel", label: "Excel" },
];

const parseOptions = (options) => {
  if (!options) return {};
  if (typeof options === "object") return options;

  try {
    return JSON.parse(options);
  } catch (_error) {
    return {};
  }
};

const toId = (value) =>
  value !== undefined && value !== null && value !== "" ? String(value) : "";

const toBool = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const csvToText = (value) => {
  if (Array.isArray(value)) return value.join(",");
  return value || "";
};

const normalizeConfig = (record = {}, fallbackModuleKey = "training") => {
  const options = parseOptions(record.options);
  const moduleKey = record.module_key || fallbackModuleKey;

  return {
    ...emptyConfig,
    ...record,
    module_key: moduleKey,
    module_label:
      record.module_label || MODULE_META[moduleKey]?.label || moduleKey,
    variant: record.variant || "",
    email_config_id: toId(record.email_config_id),
    email_temp_id: toId(record.email_temp_id),
    notify_roles: csvToText(record.notify_roles),
    extra_emails: csvToText(record.extra_emails),
    notify_in_app: toBool(record.notify_in_app),
    notify_employee: toBool(record.notify_employee),
    notify_owner: toBool(record.notify_owner),
    notify_digest: toBool(record.notify_digest),
    is_off: toBool(record.is_off),
    reminder_days_before:
      record.reminder_days_before || record.default_reminder_days || "",
    overdue_repeat_days: record.overdue_repeat_days || 7,
    frequency_days: record.frequency_days || record.email_frequency_days || "",
    next_run_date: toDateInputValue(
      record.next_run_date || record.next_reminder_date || record.send_date,
    ),
    send_date: toDateInputValue(record.send_date || record.next_run_date),
    horizon_days: record.horizon_days || "",
    max_rows: record.max_rows || "",
    effective_sender_email: record.effective_sender_email || "",
    sender_is_fallback: toBool(record.sender_is_fallback),
    email_config_email: record.email_config_email || "",
    template_name: record.template_name || "",
    template_subject: record.template_subject || "",
    template_body: record.template_body || "",
    template_to_emails: record.template_to_emails || "",
    options: {
      format: options.format || record.format || "CSV",
      enabled_modules: csvToText(options.enabled_modules),
    },
  };
};

const isPositiveInteger = (value) =>
  /^\d+$/.test(String(value)) && Number(value) > 0;

const validateCsvPositiveIntegers = (value, label, required = false) => {
  const text = String(value || "").trim();
  if (!text) return required ? `${label} is required.` : "";

  const invalid = text
    .split(",")
    .map((item) => item.trim())
    .filter((item) => !isPositiveInteger(item));

  return invalid.length
    ? `${label} must contain positive numbers separated by commas.`
    : "";
};

const validateCsvEmails = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const invalid = text
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item && !emailRegex.test(item));

  return invalid.length
    ? `Invalid email address${invalid.length > 1 ? "es" : ""}: ${invalid.join(", ")}`
    : "";
};

const moduleOptionLabel = (key, rows = []) => {
  const fromRow = rows.find((row) => row.module_key === key)?.module_label;
  return fromRow || MODULE_META[key]?.label || key;
};

const displayValue = (value) =>
  value !== undefined && value !== null && value !== "" ? String(value) : "NA";

const splitCsvValues = (value = "") =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getTemplateReview = (template = {}) => ({
  template_name:
    template.template_name || template.temp_name || template.label || "",
  template_to_emails:
    template.template_to_emails || template.to_emails || "",
  template_subject:
    template.template_subject || template.subject || "",
  template_body: template.template_body || template.body || "",
});

const hasTemplateReview = (template = {}) =>
  Boolean(
    template.template_name ||
      template.temp_name ||
      template.template_subject ||
      template.subject ||
      template.template_to_emails ||
      template.to_emails ||
      template.template_body ||
      template.body,
  );

const getReminderSummary = (record = {}) => {
  if (record.frequency_days) {
    return `Every ${record.frequency_days} day${Number(record.frequency_days) === 1 ? "" : "s"}`;
  }

  if (record.reminder_days_before) {
    return `${record.reminder_days_before} days before`;
  }

  if (record.horizon_days) {
    return `${record.horizon_days} day horizon`;
  }

  return "NA";
};

const getConfigStatusLabel = (record = {}) => {
  if (toBool(record.is_off)) return "Off";
  return toBool(record.configured) ? "Configured" : "Pending";
};

const getConfigStatusClass = (record = {}) => {
  if (toBool(record.is_off)) return "notification-config-status-off";
  return toBool(record.configured)
    ? "notification-config-status-configured"
    : "notification-config-status-pending";
};

const getRowKey = (record = {}, index) =>
  record.id ||
  `${record.module_key || "module"}-${record.variant || "default"}-${index}`;

const FIELD_LABELS = {
  variant: "Report Type",
  module_label: "Module Label",
  email_config_id: "Sender Email",
  email_temp_id: "Email Template",
  notify_in_app: "Notify In App",
  notify_employee: "Notify Employee",
  notify_owner: "Notify Owner",
  notify_digest: "Notify Digest",
  notify_roles: "Notify Roles",
  extra_emails: "Extra Emails",
  reminder_days_before: "Reminder Days",
  overdue_repeat_days: "Overdue Repeat Days",
  horizon_days: "Horizon Days",
  max_rows: "Maximum Rows",
  frequency_days: "Frequency Days",
  send_date: "Send Date",
  next_run_date: "Next Run Date",
  is_off: "Turn Off Reminders",
  format: "Format",
  enabled_modules: "Enabled Modules",
};

const getViewFieldValue = (record = {}, field) => {
  switch (field) {
    case "email_config_id":
      return (
        record.email_config_email ||
        record.effective_sender_email ||
        record.email_config_id ||
        "NA"
      );
    case "email_temp_id":
      return record.template_name || record.email_temp_id || "NA";
    case "notify_in_app":
    case "notify_employee":
    case "notify_owner":
    case "notify_digest":
    case "is_off":
      return truthyLabel(toBool(record[field]));
    case "format":
      return record.options?.format || "NA";
    case "enabled_modules":
      return displayValue(record.options?.enabled_modules);
    case "send_date":
    case "next_run_date":
      return record[field] ? displayValue(record[field]) : "NA";
    default:
      return displayValue(record[field]);
  }
};

const getNotificationViewFields = (record = {}, meta = {}) => {
  const seen = new Set();
  const fields = [
    ["Module", displayValue(record.module_label)],
    ["Module Key", displayValue(record.module_key)],
    ["Status", getConfigStatusLabel(record)],
    ...(meta.fields || []).map((field) => [
      FIELD_LABELS[field] || field,
      getViewFieldValue(record, field),
    ]),
    ["Effective Sender", displayValue(record.effective_sender_email)],
    ["Created At", formatDateTimeIST(record.created_at)],
    ["Updated At", formatDateTimeIST(record.updated_at)],
    ["Last Sent At", formatDateTimeIST(record.last_sent_at)],
  ];

  return fields.filter(([label]) => {
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};

function ReminderSettingsPage({ isSidebarExpanded, moduleType = "training" }) {
  const isCentralPage = moduleType === "all" || moduleType === "notification";
  const initialModule = isCentralPage ? "training" : moduleType;
  const [config, setConfig] = useState(() =>
    normalizeConfig({ module_key: initialModule }, initialModule),
  );
  const [allConfigs, setAllConfigs] = useState([]);
  const [moduleKeys, setModuleKeys] = useState(Object.keys(MODULE_META));
  const [isEditingConfig, setIsEditingConfig] = useState(!isCentralPage);
  const [isViewingConfig, setIsViewingConfig] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [emailConfigOptions, setEmailConfigOptions] = useState([]);
  const [emailTemplateOptions, setEmailTemplateOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const meta = MODULE_META[config.module_key] || {
    title: `${config.module_label || config.module_key} Settings`,
    label: config.module_label || config.module_key,
    fields: ["reminder_days_before", "overdue_repeat_days", "notify"],
  };

  const moduleOptions = useMemo(() => {
    const keys = new Set([...Object.keys(MODULE_META), ...moduleKeys]);
    return Array.from(keys).map((key) => ({
      value: key,
      label: moduleOptionLabel(key, allConfigs),
    }));
  }, [allConfigs, moduleKeys]);

  const scheduledVariantOptions = useMemo(
    () =>
      allConfigs
        .filter((row) => row.module_key === "scheduled_report" && row.variant)
        .map((row) => ({
          value: row.variant,
          label: row.variant,
        })),
    [allConfigs],
  );

  const filteredConfigs = useMemo(() => {
    const searchText = searchInput.trim().toLowerCase();
    if (!searchText) return allConfigs;

    return allConfigs.filter((record) =>
      [
        record.module_label,
        record.module_key,
        record.variant,
        record.effective_sender_email,
        record.email_config_email,
        record.template_name,
        record.template_subject,
        record.notify_roles,
        getConfigStatusLabel(record),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchText)),
    );
  }, [allConfigs, searchInput]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadEmailTemplates = useCallback(async () => {
    const templatesResponse = await hrRecordsApi.getEmailTemplates();
    const nextTemplates = buildEmailTemplateOptions(
      templatesResponse.templates || [],
    );
    setEmailTemplateOptions(nextTemplates);
    return nextTemplates;
  }, []);

  const loadSupportData = useCallback(async () => {
    const [configsResponse] = await Promise.all([
      hrRecordsApi.getEmailConfigs({ page: 1, per_page: 500 }),
      loadEmailTemplates(),
    ]);

    setEmailConfigOptions(buildEmailConfigOptions(configsResponse.configs || []));
  }, [loadEmailTemplates]);

  const refreshAllConfigs = useCallback(async () => {
    const response = await hrRecordsApi.listNotificationConfigs();
    setAllConfigs(response.records || []);
    setModuleKeys(response.modules || Object.keys(MODULE_META));
    return response.records || [];
  }, []);

  const loadModuleConfig = useCallback(async (moduleKey, variant = "") => {
    if (moduleKey === "scheduled_report" && !variant) {
      const rows = await refreshAllConfigs();
      const firstScheduled = rows.find((row) => row.module_key === moduleKey);
      setConfig(
        normalizeConfig(firstScheduled || { module_key: moduleKey }, moduleKey),
      );
      return;
    }

    const response = await hrRecordsApi.getNotificationConfig(
      moduleKey,
      variant ? { variant } : {},
    );
    setConfig(normalizeConfig(response, moduleKey));
  }, [refreshAllConfigs]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        await loadSupportData();

        if (isCentralPage) {
          const rows = await refreshAllConfigs();
          const firstRow =
            rows.find((row) => row.module_key !== "scheduled_report") ||
            rows[0] ||
            { module_key: "training" };
          setConfig(normalizeConfig(firstRow, firstRow.module_key));
          setIsEditingConfig(false);
          setIsViewingConfig(false);
        } else {
          await loadModuleConfig(moduleType);
          refreshAllConfigs().catch(() => {});
          setIsEditingConfig(true);
          setIsViewingConfig(false);
        }
      } catch (error) {
        console.error("Failed to load reminder settings:", error);
        showSnackbar(
          getApiErrorMessage(error, "Failed to load reminder settings"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    isCentralPage,
    loadModuleConfig,
    loadSupportData,
    moduleType,
    refreshAllConfigs,
  ]);

  const handleChange = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setConfig((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        [field]: value,
      },
    }));
  };

  const handleTemplateChange = (option) => {
    setErrors((prev) => ({ ...prev, email_temp_id: "" }));
    setConfig((prev) => ({
      ...prev,
      email_temp_id: option?.value || "",
      ...getTemplateReview(option || {}),
    }));
  };

  const handleModuleChange = async (option) => {
    if (!option?.value) return;

    try {
      setLoading(true);
      setErrors({});
      await loadModuleConfig(option.value);
    } catch (error) {
      console.error("Failed to load module configuration:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load module configuration"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVariantSelect = async (option) => {
    const variant = option?.value || "";
    handleChange("variant", variant);
    if (!variant) return;

    try {
      setLoading(true);
      await loadModuleConfig("scheduled_report", variant);
    } catch (error) {
      console.error("Failed to load scheduled report configuration:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load scheduled report configuration"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshConfigs = async () => {
    try {
      setLoading(true);
      await refreshAllConfigs();
    } catch (error) {
      console.error("Failed to refresh notification configurations:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to refresh notification configurations"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = async (record) => {
    const moduleKey = record?.module_key;
    if (!moduleKey) return;

    try {
      setLoading(true);
      setErrors({});
      await Promise.all([
        loadEmailTemplates(),
        loadModuleConfig(moduleKey, record.variant || ""),
      ]);
      setIsEditingConfig(true);
      setIsViewingConfig(false);
    } catch (error) {
      console.error("Failed to load notification configuration:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load notification configuration"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewConfig = async (record) => {
    const moduleKey = record?.module_key;
    if (!moduleKey) return;

    try {
      setLoading(true);
      setErrors({});
      await loadModuleConfig(moduleKey, record.variant || "");
      setIsViewingConfig(true);
      setIsEditingConfig(false);
    } catch (error) {
      console.error("Failed to load notification configuration:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load notification configuration"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setErrors({});
    setIsViewingConfig(false);
    setIsEditingConfig(false);
  };

  const buildPayload = () => {
    const fields = new Set(meta.fields || []);
    const payload = {};

    if (fields.has("variant")) {
      payload.variant = config.variant.trim();
    }

    if (fields.has("module_label")) {
      payload.module_label = config.module_label.trim();
    }

    if (fields.has("email_config_id")) {
      payload.email_config_id = config.email_config_id || null;
    }

    if (fields.has("email_temp_id")) {
      payload.email_temp_id = config.email_temp_id || null;
    }

    if (fields.has("notify_roles")) {
      payload.notify_roles = config.notify_roles.trim();
    }

    if (fields.has("notify_in_app")) {
      payload.notify_in_app = config.notify_in_app ? 1 : 0;
    }

    if (fields.has("notify_employee")) {
      payload.notify_employee = config.notify_employee ? 1 : 0;
    }

    if (fields.has("notify_owner")) {
      payload.notify_owner = config.notify_owner ? 1 : 0;
    }

    if (fields.has("notify_digest")) {
      payload.notify_digest = config.notify_digest ? 1 : 0;
    }

    if (fields.has("is_off")) {
      payload.is_off = config.is_off ? 1 : 0;
    }

    if (fields.has("reminder_days_before")) {
      payload.reminder_days_before = config.reminder_days_before.trim();
    }

    if (fields.has("overdue_repeat_days")) {
      payload.overdue_repeat_days = config.overdue_repeat_days
        ? Number(config.overdue_repeat_days)
        : null;
    }

    if (fields.has("frequency_days")) {
      payload.frequency_days = Number(config.frequency_days);
    }

    if (fields.has("next_run_date")) {
      payload.next_run_date = config.next_run_date || null;
    }

    if (fields.has("send_date")) {
      payload.send_date = config.send_date || null;
    }

    if (fields.has("extra_emails")) {
      payload.extra_emails = config.extra_emails.trim();
    }

    if (fields.has("horizon_days")) {
      payload.horizon_days = config.horizon_days
        ? Number(config.horizon_days)
        : null;
    }

    if (fields.has("max_rows")) {
      payload.max_rows = config.max_rows ? Number(config.max_rows) : null;
    }

    const options = {};
    if (fields.has("format")) {
      options.format = config.options.format || "CSV";
    }
    if (fields.has("enabled_modules")) {
      options.enabled_modules = String(config.options.enabled_modules || "").trim();
    }
    if (Object.keys(options).length) {
      payload.options = options;
    }

    return payload;
  };

  const validate = () => {
    const nextErrors = {};
    const fields = new Set(meta.fields || []);

    if (fields.has("variant") && !config.variant.trim()) {
      nextErrors.variant = "Report type is required.";
    }

    if (fields.has("module_label") && !config.module_label.trim()) {
      nextErrors.module_label = "Module label is required.";
    }

    if (fields.has("email_config_id") && !config.is_off && !config.email_config_id) {
      nextErrors.email_config_id = "Sender email is required.";
    }

    if (fields.has("email_temp_id") && !config.is_off && !config.email_temp_id) {
      nextErrors.email_temp_id = "Email template is required.";
    }

    if (
      fields.has("notify_roles") &&
      !config.is_off &&
      !config.notify_roles.trim()
    ) {
      nextErrors.notify_roles = "Notify roles are required.";
    }

    if (fields.has("reminder_days_before")) {
      const error = validateCsvPositiveIntegers(
        config.reminder_days_before,
        "Reminder days",
        !config.is_off,
      );
      if (error) nextErrors.reminder_days_before = error;
    }

    if (
      fields.has("overdue_repeat_days") &&
      !config.is_off &&
      !isPositiveInteger(config.overdue_repeat_days)
    ) {
      nextErrors.overdue_repeat_days =
        "Overdue repeat days must be a positive number.";
    }

    if (
      fields.has("frequency_days") &&
      !config.is_off &&
      !isPositiveInteger(config.frequency_days)
    ) {
      nextErrors.frequency_days = "Frequency days must be a positive number.";
    }

    if (fields.has("send_date") && !config.is_off && !config.send_date) {
      nextErrors.send_date = "Send date is required.";
    }

    if (fields.has("extra_emails")) {
      const error = validateCsvEmails(config.extra_emails);
      if (error) nextErrors.extra_emails = error;
    }

    if (
      fields.has("horizon_days") &&
      !config.is_off &&
      !isPositiveInteger(config.horizon_days)
    ) {
      nextErrors.horizon_days = "Horizon days must be a positive number.";
    }

    if (
      fields.has("max_rows") &&
      !config.is_off &&
      !isPositiveInteger(config.max_rows)
    ) {
      nextErrors.max_rows = "Maximum rows must be a positive number.";
    }

    if (
      fields.has("enabled_modules") &&
      !config.is_off &&
      !String(config.options.enabled_modules || "").trim()
    ) {
      nextErrors.enabled_modules = "Enabled modules are required.";
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      showSnackbar(
        Object.values(validationErrors)[0] ||
          "Please correct the highlighted fields.",
        "error",
      );
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      const params = meta.usesVariant ? { variant: payload.variant } : {};
      const response = await hrRecordsApi.updateNotificationConfig(
        config.module_key,
        payload,
        params,
      );

      if (response.record) {
        setConfig(normalizeConfig(response.record, config.module_key));
      }
      await refreshAllConfigs();
      showSnackbar("Notification settings updated successfully");
      if (isCentralPage) {
        setIsEditingConfig(false);
      }
    } catch (error) {
      console.error("Failed to save reminder settings:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to save reminder settings"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const fields = new Set(meta.fields || []);
  const selectedTemplateReview = useMemo(() => {
    const selectedTemplate = emailTemplateOptions.find(
      (option) => String(option.value) === String(config.email_temp_id),
    );
    return hasTemplateReview(selectedTemplate)
      ? getTemplateReview(selectedTemplate)
      : getTemplateReview(config);
  }, [config, emailTemplateOptions]);

  if (isCentralPage && !isEditingConfig && !isViewingConfig) {
    return (
      <>
        <div
          className={`data-list-content-container page-content ${
            isSidebarExpanded ? "expanded" : "collapsed"
          }`}
        >
          <div className="data-list-content">
            <div className="data-list-header">
              <div className="data-list-header-content notification-config-header-content">
                <PageBackTitle>Notification Configurations</PageBackTitle>
                <div className="data-list-heading-content-right-side notification-config-header-tools">
                  <div className="search-bar-container">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      className="search-bar"
                    />
                    <IoSearchOutline className="search-bar-icon" />
                  </div>

                  <Tooltip title="Refresh List" arrow>
                    <button
                      className="refresh-button"
                      type="button"
                      onClick={handleRefreshConfigs}
                      disabled={loading}
                    >
                      <TbRefresh className="refresh-icon" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="header-separator"></div>

            <div className="data-list-body">
              <div className="data-list-scroll-body">
                <div className="data-list-table notification-config-table">
                  {loading ? (
                    <div className="loader-container">
                      <div className="loader"></div>
                      <div>Loading...</div>
                    </div>
                  ) : filteredConfigs.length > 0 ? (
                    <table className="table multilevel-data">
                      <thead>
                        <tr>
                          <th className="center-alignment">SR. NO.</th>
                          <th>MODULE</th>
                          <th>TYPE</th>
                          <th className="center-alignment">STATUS</th>
                          <th>SENDER</th>
                          <th>TEMPLATE</th>
                          <th>NOTIFY ROLES</th>
                          <th>REMINDER</th>
                          <th>NEXT RUN</th>
                          <th>UPDATED AT</th>
                          <th className="center-alignment">ACTION</th>
                        </tr>
                      </thead>
                      <tbody className="data-list-scroll-bodys">
                        {filteredConfigs.map((record, index) => (
                          <tr key={getRowKey(record, index)} className="hoverable-row">
                            <td className="center-alignment">{index + 1}</td>
                            <td>{displayValue(record.module_label)}</td>
                            <td>{record.variant || "Default"}</td>
                            <td className="center-alignment">
                              <span
                                className={`notification-config-status ${getConfigStatusClass(
                                  record,
                                )}`}
                              >
                                {getConfigStatusLabel(record)}
                              </span>
                            </td>
                            <td className="notification-config-wrap-cell">
                              {displayValue(
                                record.effective_sender_email ||
                                  record.email_config_email,
                              )}
                            </td>
                            <td>{displayValue(record.template_name)}</td>
                            <td className="notification-config-truncate">
                              {displayValue(record.notify_roles)}
                            </td>
                            <td>{getReminderSummary(record)}</td>
                            <td>
                              {formatDateTimeIST(
                                record.next_run_date || record.send_date,
                              )}
                            </td>
                            <td>{formatDateTimeIST(record.updated_at)}</td>
                            <td className="center-alignment">
                              <div className="notification-config-row-actions">
                                <button
                                  className="notification-config-view-button"
                                  type="button"
                                  onClick={() => handleViewConfig(record)}
                                  disabled={loading}
                                >
                                  View
                                </button>
                                  <button
                                    className="notification-config-view-button"
                                    type="button"
                                    onClick={() => handleEditConfig(record)}
                                    disabled={loading}
                                  >
                                    Edit
                                  </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-users-container">
                      <div className="no-users-message">
                        No Notification Configuration Found
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
        </Snackbar>
      </>
    );
  }

  if (isCentralPage && isViewingConfig) {
    const viewFields = getNotificationViewFields(config, meta);

    return (
      <>
        <div
          className={`invoice-request-content-container page-content ${
            isSidebarExpanded ? "expanded" : "collapsed"
          }`}
        >
          <div className="invoice-request-header-content">
            <div className="invoice-request-heading-content-left-side">
              <Tooltip title="Back" arrow>
                <IoMdArrowBack
                  className="back-company"
                  onClick={handleBackToList}
                />
              </Tooltip>
              Notification Configuration Details
            </div>
          </div>

          <div className="data-form-body-voyage">
            {loading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <>
                <div className="project-detailss">
                  <div className="project-detail-arround">
                    <div className="project-details-left-side">
                      {viewFields.map(([label, value]) => (
                        <div className="project-detail" key={label}>
                          <div className="detail-header">{label}:</div>
                          <div className="detail-part-voyage">
                            {value || "NA"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {hasTemplateReview(selectedTemplateReview) && (
                  <>
                    <br />
                    <div className="hr-records-section">
                      <div className="hr-records-section-title">
                        Selected Template Review
                      </div>
                      <div className="project-detailss notification-config-view-section">
                        <div className="project-detail-arround">
                          <div className="project-details-left-side">
                            {[
                              ["Template Name", selectedTemplateReview.template_name],
                              ["To Emails", selectedTemplateReview.template_to_emails],
                              ["Subject", selectedTemplateReview.template_subject],
                              ["Body", selectedTemplateReview.template_body],
                            ].map(([label, value]) => (
                              <div
                                className="project-detail"
                                key={`template-${label}`}
                              >
                                <div className="detail-header">{label}:</div>
                                <div className="detail-part-voyage notification-config-view-long-text">
                                  {value || "NA"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                {isCentralPage
                  ? `Edit ${config.module_label || meta.label} Configuration`
                  : meta.title}
              </div>
              
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-form-body hr-records-settings-body">
            {loading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="hr-records-section">
                  <div className="hr-records-section-title">Email Reminder Setup</div>
                  <div className="hr-records-grid-two">
                    {isCentralPage && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Module</label>
                        <Select
                          className="hr_config_select"
                          options={
                            moduleOptions.length
                              ? moduleOptions
                              : DEFAULT_MODULE_OPTIONS
                          }
                          value={getSelectedOption(
                            moduleOptions,
                            config.module_key,
                            "Module",
                          )}
                          onChange={handleModuleChange}
                          placeholder="Select module"
                          isDisabled
                          isSearchable
                        />
                      </div>
                    )}

                    {fields.has("variant") && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Report Type</label>
                        <Select
                          className="hr_config_select"
                          options={scheduledVariantOptions}
                          value={
                            config.variant
                              ? { value: config.variant, label: config.variant }
                              : null
                          }
                          onChange={handleVariantSelect}
                          onInputChange={(value, action) => {
                            if (action.action === "input-change") {
                              handleChange("variant", value);
                            }
                          }}
                          placeholder="Select or type report type"
                          isClearable
                          isSearchable
                        />
                        {errors.variant && (
                          <div className="error-message">{errors.variant}</div>
                        )}
                      </div>
                    )}

                    {fields.has("module_label") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Module Label</label>
                        <input
                          type="text"
                          value={config.module_label}
                          onChange={(event) =>
                            handleChange("module_label", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.module_label && (
                          <div className="error-message">{errors.module_label}</div>
                        )}
                      </div>
                    )}

                    {fields.has("email_config_id") && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Sender Email</label>
                        <Select
                          className="hr_config_select"
                          options={emailConfigOptions}
                          value={getSelectedOption(
                            emailConfigOptions,
                            config.email_config_id,
                            "Sender Email",
                          )}
                          onChange={(option) =>
                            handleChange("email_config_id", option?.value || "")
                          }
                          placeholder="Select sender email"
                          isClearable
                          isSearchable
                          noOptionsMessage={() => "No sender emails found"}
                        />
                        {errors.email_config_id && (
                          <div className="error-message">
                            {errors.email_config_id}
                          </div>
                        )}
                      </div>
                    )}

                    {fields.has("email_temp_id") && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Email Template</label>
                        <Select
                          className="hr_config_select"
                          options={emailTemplateOptions}
                          value={getSelectedOption(
                            emailTemplateOptions,
                            config.email_temp_id,
                            "Email Template",
                          )}
                          onChange={handleTemplateChange}
                          placeholder="Select email template"
                          isClearable
                          isSearchable
                          noOptionsMessage={() => "No email templates found"}
                        />
                        {errors.email_temp_id && (
                          <div className="error-message">
                            {errors.email_temp_id}
                          </div>
                        )}
                      </div>
                    )}

                    {fields.has("notify_roles") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Notify Roles</label>
                        <input
                          type="text"
                          value={config.notify_roles}
                          onChange={(event) =>
                            handleChange("notify_roles", event.target.value)
                          }
                          placeholder="e.g. admin,hr"
                          className="hr_config_fields"
                        />
                        {errors.notify_roles && (
                          <div className="error-message">{errors.notify_roles}</div>
                        )}
                      </div>
                    )}

                    {fields.has("reminder_days_before") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Reminder Days</label>
                        <input
                          type="text"
                          value={config.reminder_days_before}
                          onChange={(event) =>
                            handleChange("reminder_days_before", event.target.value)
                          }
                          placeholder="e.g. 30,15,7"
                          className="hr_config_fields"
                        />
                        {errors.reminder_days_before && (
                          <div className="error-message">
                            {errors.reminder_days_before}
                          </div>
                        )}
                      </div>
                    )}

                    {fields.has("overdue_repeat_days") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Overdue Repeat Days</label>
                        <input
                          type="number"
                          min="1"
                          value={config.overdue_repeat_days}
                          onChange={(event) =>
                            handleChange("overdue_repeat_days", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.overdue_repeat_days && (
                          <div className="error-message">
                            {errors.overdue_repeat_days}
                          </div>
                        )}
                      </div>
                    )}

                    {fields.has("frequency_days") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Frequency Days</label>
                        <input
                          type="number"
                          min="1"
                          value={config.frequency_days}
                          onChange={(event) =>
                            handleChange("frequency_days", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.frequency_days && (
                          <div className="error-message">{errors.frequency_days}</div>
                        )}
                      </div>
                    )}

                    {fields.has("next_run_date") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Next Run Date</label>
                        <input
                          type="date"
                          value={config.next_run_date}
                          onChange={(event) =>
                            handleChange("next_run_date", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                      </div>
                    )}

                    {fields.has("send_date") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Send Date</label>
                        <input
                          type="date"
                          value={config.send_date}
                          onChange={(event) =>
                            handleChange("send_date", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.send_date && (
                          <div className="error-message">{errors.send_date}</div>
                        )}
                      </div>
                    )}

                    {fields.has("format") && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Format</label>
                        <Select
                          className="hr_config_select"
                          options={formatOptions}
                          value={
                            formatOptions.find(
                              (option) => option.value === config.options.format,
                            ) || formatOptions[0]
                          }
                          onChange={(option) =>
                            handleOptionChange("format", option?.value || "CSV")
                          }
                        />
                      </div>
                    )}

                    {fields.has("enabled_modules") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Enabled Modules</label>
                        <input
                          type="text"
                          value={config.options.enabled_modules}
                          onChange={(event) =>
                            handleOptionChange("enabled_modules", event.target.value)
                          }
                          placeholder="e.g. statutory,hr"
                          className="hr_config_fields"
                        />
                        {errors.enabled_modules && (
                          <div className="error-message">
                            {errors.enabled_modules}
                          </div>
                        )}
                      </div>
                    )}

                    {fields.has("extra_emails") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Extra Emails</label>
                        <input
                          type="text"
                          value={config.extra_emails}
                          onChange={(event) =>
                            handleChange("extra_emails", event.target.value)
                          }
                          placeholder="name@example.com,team@example.com"
                          className="hr_config_fields"
                        />
                        {errors.extra_emails && (
                          <div className="error-message">{errors.extra_emails}</div>
                        )}
                      </div>
                    )}

                    {fields.has("horizon_days") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Horizon Days</label>
                        <input
                          type="number"
                          min="1"
                          value={config.horizon_days}
                          onChange={(event) =>
                            handleChange("horizon_days", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.horizon_days && (
                          <div className="error-message">{errors.horizon_days}</div>
                        )}
                      </div>
                    )}

                    {fields.has("max_rows") && (
                      <div className="form-group">
                        <label style={{ width: "40%" }}>Maximum Rows</label>
                        <input
                          type="number"
                          min="1"
                          value={config.max_rows}
                          onChange={(event) =>
                            handleChange("max_rows", event.target.value)
                          }
                          className="hr_config_fields"
                        />
                        {errors.max_rows && (
                          <div className="error-message">{errors.max_rows}</div>
                        )}
                      </div>
                    )}

                    {[
                      ["notify_in_app", "Notify In App"],
                      ["notify_employee", "Notify Employee"],
                      ["notify_owner", "Notify Owner"],
                      ["notify_digest", "Notify Digest"],
                    ]
                      .filter(([field]) => fields.has(field))
                      .map(([field, label]) => (
                        <div className="form-group-select" key={field}>
                          <label style={{ width: "40%" }}>{label}</label>
                          <Select
                            className="hr_config_select"
                            options={yesNoOptions}
                            value={{
                              value: config[field],
                              label: truthyLabel(config[field]),
                            }}
                            onChange={(option) => handleChange(field, option?.value)}
                          />
                        </div>
                      ))}

                    {fields.has("is_off") && (
                      <div className="form-group-select">
                        <label style={{ width: "40%" }}>Turn Off Reminders</label>
                        <Select
                          className="hr_config_select"
                          options={yesNoOptions}
                          value={{
                            value: config.is_off,
                            label: truthyLabel(config.is_off),
                          }}
                          onChange={(option) =>
                            handleChange("is_off", option?.value || false)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {hasTemplateReview(selectedTemplateReview) && (
                  <div className="hr-records-section">
                    <div className="hr-records-section-title">
                      Selected Template Review
                    </div>
                    <div className="notification-template-review-card">
                      <div className="notification-template-review-header">
                        <div>
                          <div className="notification-template-review-label">
                            Template Name
                          </div>
                          <div className="notification-template-review-title">
                            {selectedTemplateReview.template_name || "NA"}
                          </div>
                        </div>
                      </div>

                      <div className="notification-template-review-grid">
                        <div className="notification-template-review-item">
                          <div className="notification-template-review-label">
                            To Emails
                          </div>
                          <div className="notification-template-review-chips">
                            {splitCsvValues(
                              selectedTemplateReview.template_to_emails,
                            ).length ? (
                              splitCsvValues(
                                selectedTemplateReview.template_to_emails,
                              ).map((email) => (
                                <span
                                  className="notification-template-review-chip"
                                  key={email}
                                >
                                  {email}
                                </span>
                              ))
                            ) : (
                              <span className="notification-template-review-empty">
                                NA
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="notification-template-review-item">
                          <div className="notification-template-review-label">
                            Subject
                          </div>
                          <div className="notification-template-review-subject">
                            {selectedTemplateReview.template_subject || "NA"}
                          </div>
                        </div>
                      </div>

                      <div className="notification-template-review-body">
                        <div className="notification-template-review-label">
                          Body
                        </div>
                        <pre>{selectedTemplateReview.template_body || "NA"}</pre>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          <div className="footer">
            <div className="button-group">
              {isCentralPage && (
                <button
                  className="person-user-cancel-button notification-config-back-button"
                  type="button"
                  onClick={handleBackToList}
                  disabled={saving}
                  style={{marginRight:"10px"}}
                >
                  Back
                </button>
              )}
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default ReminderSettingsPage;
